import { Card, Col, Divider, Flex, Row } from 'antd'
import dayjs from 'dayjs'

import { useClassifierServiceGetCountryClassifierElementListEndpoint } from 'src/api/queries'
import { EpidemiologyCaseForm058E_PatientSection } from 'src/api/requests'
import { Text } from 'src/components'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type Props = {
  patient: EpidemiologyCaseForm058E_PatientSection
}

export const EpidemiologyPatientInformationDetailsCard = ({
  patient
}: Props) => {
  const { t, currentLang } = useLocales()

  const { styles } = useStyles()

  const { data: countries } =
    useClassifierServiceGetCountryClassifierElementListEndpoint()

  // The family-doctor institution is no longer collected; older forms may still have it.
  const medicalInstitution = patient.medicalInstitution
  const hasMedicalInstitution = [
    medicalInstitution?.title,
    medicalInstitution?.code,
    medicalInstitution?.localityId,
    medicalInstitution?.street,
    medicalInstitution?.house
  ].some(Boolean)

  return (
    <Card title={t('epidemiology.patient_card_title')}>
      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.patient_status.title')}</Text>
            <Text weight={400}>
              {t(`epidemiology.patient_status.${patient.identityStatus}`)}
            </Text>
          </Flex>
        </Col>

        {patient.idnp && (
          <Col span={8}>
            <Flex vertical gap={4}>
              <Text>{t('form.idnp')}</Text>
              <Text weight={400}>{patient.idnp}</Text>
            </Flex>
          </Col>
        )}

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('form.birth_date')}</Text>
            <Text weight={400}>
              {dayjs(patient.birthDate).format(dayMonthYearFormatWithDot)}
            </Text>
          </Flex>
        </Col>

        {patient.lastName && (
          <Col span={8}>
            <Flex vertical gap={4}>
              <Text>{t('form.last_name')}</Text>
              <Text weight={400}>{patient.lastName}</Text>
            </Flex>
          </Col>
        )}

        {patient.firstName && (
          <Col span={8}>
            <Flex vertical gap={4}>
              <Text>{t('form.first_name')}</Text>
              <Text weight={400}>{patient.firstName}</Text>
            </Flex>
          </Col>
        )}

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('form.gender')}</Text>
            <Text weight={400}>{t(`form.${patient.gender}`)}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('common.citizenship')}</Text>
            <Text weight={400}>
              {
                countries?.data?.find(
                  (c) => c.isoCode === patient.citizenship
                )?.[currentLang.value]
              }
            </Text>
          </Flex>
        </Col>

        <Col span={16}>
          <Flex vertical gap={4}>
            <Text>{t('form.social_status')}</Text>
            <Text weight={400}>
              {patient.socialStatus?.name?.[currentLang.value] ?? '-'}
            </Text>
          </Flex>
        </Col>

        <Divider className={styles.divider} />

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.work_place')}</Text>
            <Text weight={400}>{patient.workInstitution?.location}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('organizational_structure.name')}</Text>
            <Text weight={400}>{patient.workInstitution?.title}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('cases.institution_address')}</Text>
            <Text weight={400}>{patient.workInstitution?.address}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.work_function')}</Text>
            <Text weight={400}>{patient.workInstitution?.function}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.last_present_date')}</Text>
            <Text weight={400}>
              {dayjs(patient.workInstitution?.lastPresentDate).format(
                dayMonthYearFormatWithDot
              )}
            </Text>
          </Flex>
        </Col>

        <Divider className={styles.divider} />

        <Col span={24}>
          <Text size="xl">{t('epidemiology.address')}</Text>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('common.country')}</Text>
            <Text weight={400}>
              {
                countries?.data?.find(
                  (c) => c.isoCode === patient.currentAddress?.country
                )?.[currentLang.value]
              }
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('organizational_structure.city')}</Text>
            <Text weight={400}>
              {patient.currentAddress?.cuatmLocality?.name?.[
                currentLang.value
              ] || '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('form.street')}</Text>
            <Text weight={400}>{patient.currentAddress?.street || '-'}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.street_number')}</Text>
            <Text weight={400}>
              {patient.currentAddress?.streetNumber || '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.apartment_number')}</Text>
            <Text weight={400}>
              {patient.currentAddress?.apartmentNumber || '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.residence_environment')}</Text>
            <Text weight={400}>
              {patient.currentAddress?.residenceEnvironment?.name?.[
                currentLang.value
              ] || '-'}
            </Text>
          </Flex>
        </Col>

        <Divider className={styles.divider} />

        <Col span={24}>
          <Text size="xl">{t('epidemiology.residential_address')}</Text>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('common.country')}</Text>
            <Text weight={400}>
              {
                countries?.data?.find(
                  (c) => c.isoCode === patient.residenceAddress?.country
                )?.[currentLang.value]
              }
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('organizational_structure.city')}</Text>
            <Text weight={400}>
              {patient.residenceAddress?.cuatmLocality?.name?.[
                currentLang.value
              ] || '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('form.street')}</Text>
            <Text weight={400}>{patient.residenceAddress?.street || '-'}</Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.street_number')}</Text>
            <Text weight={400}>
              {patient.residenceAddress?.streetNumber || '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.apartment_number')}</Text>
            <Text weight={400}>
              {patient.residenceAddress?.apartmentNumber || '-'}
            </Text>
          </Flex>
        </Col>

        <Col span={8}>
          <Flex vertical gap={4}>
            <Text>{t('epidemiology.residence_environment')}</Text>
            <Text weight={400}>
              {patient.residenceAddress?.residenceEnvironment?.name?.[
                currentLang.value
              ] || '-'}
            </Text>
          </Flex>
        </Col>

        {hasMedicalInstitution && (
          <>
            <Divider className={styles.divider} />

            <Col span={16}>
              <Flex vertical gap={4}>
                <Text>{t('epidemiology.patient_institution')}</Text>
                <Text weight={400}>{medicalInstitution?.title || '-'}</Text>
              </Flex>
            </Col>

            <Col span={8}>
              <Flex vertical gap={4}>
                <Text>{t('common.code')}</Text>
                <Text weight={400}>{medicalInstitution?.code || '-'}</Text>
              </Flex>
            </Col>

            <Col span={24}>
              <Text>{t('epidemiology.patient_institution_address')}: </Text>
            </Col>

            <Col span={8}>
              <Flex vertical gap={4}>
                <Text>{t('organizational_structure.city')}</Text>
                <Text weight={400}>{medicalInstitution?.locality}</Text>
              </Flex>
            </Col>

            <Col span={8}>
              <Flex vertical gap={4}>
                <Text>{t('form.street')}</Text>
                <Text weight={400}>{medicalInstitution?.street}</Text>
              </Flex>
            </Col>

            <Col span={8}>
              <Flex vertical gap={4}>
                <Text>{t('epidemiology.street_number')}</Text>
                <Text weight={400}>{medicalInstitution?.house}</Text>
              </Flex>
            </Col>
          </>
        )}
      </Row>
    </Card>
  )
}
